// The KNOWLEDGE-TREE RAIL (Signal re-layout) — the design's left rail: the focused Node's connection
// diagram, a whole-tree search, and the tree of areas and Nodes. Every tree row comes from a level the owning
// `memory` module actually returned (see memory-tree.ts); the rail invents nothing.
//
// WHAT THE DESIGN SHOWS THAT THE DATA DOES NOT SUPPORT, and is therefore ABSENT here:
//   • the maturity legend (Core / Validated / Unconfirmed) and the maturity-coloured dots — `maturity` is
//     not a field, and a tree child carries only id/title/path, so nothing here could colour a dot honestly;
//   • the NEW badge — there is no new-this-week signal on a Node.
// Search results are a separate bounded metadata projection from Memory's index, so collapsed branches are
// searchable without loading their levels or exposing article bodies.

import { Icon } from '../../app/Icon.tsx';
import type { MemoryTreeRow } from './memory-tree.ts';
import type { MemorySearchResultView } from '../know-projections.ts';
import type { MemorySearchState } from './useMemorySearch.ts';
import { MAX_MEMORY_SEARCH_QUERY_CHARS } from './memory-search.ts';

export interface MemoryTreeRailProps {
  readonly rows: readonly MemoryTreeRow[];
  /** True once at least one level has actually been read. Until then the rail says nothing about the
   *  wiki's contents — "no areas yet" would be a claim, and the reader has not been told anything yet. */
  readonly loaded: boolean;
  readonly focusedNodeId: string | undefined;
  readonly query: string;
  readonly onQuery: (query: string) => void;
  readonly search: MemorySearchState;
  readonly onToggleArea: (areaPath: string) => void;
  readonly onFocusNode: (nodeId: string) => void;
  readonly onFocusSearchResult: (result: MemorySearchResultView) => void;
  /** The connection diagram slot — omitted entirely by the pane when the focused Node has no edges. */
  readonly children?: React.ReactNode;
}

/** One global result — title plus canonical path so duplicate titles remain distinguishable. */
function SearchResultRow({
  result,
  selected,
  onFocus,
}: {
  readonly result: MemorySearchResultView;
  readonly selected: boolean;
  readonly onFocus: (result: MemorySearchResultView) => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className={selected ? 'mw-row node sel' : 'mw-row node'}
      data-memory-search-result={result.id}
      aria-current={selected ? 'true' : undefined}
      onClick={() => onFocus(result)}
    >
      <span className="mw-dot" aria-hidden="true" />
      <span className="lbl">{result.title}</span>
      <span className="mw-count mw-search-path">{result.path}</span>
    </button>
  );
}

/** The active global-search branch of the rail. */
function SearchRows({
  search,
  focusedNodeId,
  onFocus,
}: {
  readonly search: MemorySearchState;
  readonly focusedNodeId: string | undefined;
  readonly onFocus: (result: MemorySearchResultView) => void;
}): React.JSX.Element {
  if (search.loading) {
    return (
      <div className="empty-line" role="status">
        Searching memory…
      </div>
    );
  }
  if (search.error === 'invalid') {
    return (
      <div className="empty-line" role="status">
        That search is too long.
      </div>
    );
  }
  if (search.error === 'unavailable') {
    return (
      <div className="empty-line" role="status">
        Memory search is unavailable.
      </div>
    );
  }
  if (search.results.length === 0) {
    return <div className="empty-line">No memory titles or paths match that search.</div>;
  }
  return (
    <>
      {search.results.map((result) => (
        <SearchResultRow
          key={`${result.id}:${result.path}`}
          result={result}
          selected={result.id === focusedNodeId}
          onFocus={onFocus}
        />
      ))}
    </>
  );
}

/** Indent one row by its depth (the tree's only geometry that varies per row). */
function indentOf(depth: number): string {
  return `${String(0.5 + depth * 0.75)}rem`;
}

/** One area row — a chevron, the area name, and its child count once the level has been read. */
function AreaRow({
  row,
  onToggleArea,
}: {
  readonly row: MemoryTreeRow;
  readonly onToggleArea: (areaPath: string) => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className="mw-row area"
      data-tree-area={row.id}
      aria-expanded={row.open}
      style={{ paddingLeft: indentOf(row.depth) }}
      onClick={() => onToggleArea(row.id)}
    >
      <span className={row.open ? 'mw-chev open' : 'mw-chev'} aria-hidden="true">
        <Icon name="chevron" size={11} />
      </span>
      <span className="lbl">{row.label}</span>
      {row.count !== undefined ? <span className="mw-count">{row.count}</span> : null}
    </button>
  );
}

/** One Node row — focusing it re-reads the projection at that Node (never a client-side mutation). */
function NodeRow({
  row,
  onFocusNode,
}: {
  readonly row: MemoryTreeRow;
  readonly onFocusNode: (nodeId: string) => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className={row.selected ? 'mw-row node sel' : 'mw-row node'}
      data-tree-node={row.id}
      aria-current={row.selected ? 'true' : undefined}
      style={{ paddingLeft: indentOf(row.depth + 1) }}
      onClick={() => onFocusNode(row.id)}
    >
      <span className="mw-dot" aria-hidden="true" />
      <span className="lbl">{row.label}</span>
    </button>
  );
}

/** The ordinary incrementally loaded tree-navigation branch of the rail. */
function TreeRows({
  rows,
  loaded,
  onToggleArea,
  onFocusNode,
}: Pick<
  MemoryTreeRailProps,
  'rows' | 'loaded' | 'onToggleArea' | 'onFocusNode'
>): React.JSX.Element {
  if (rows.length === 0 && loaded) {
    return <div className="empty-line">This wiki has no areas or nodes yet.</div>;
  }
  return (
    <>
      {rows.map((row) =>
        row.kind === 'area' ? (
          <AreaRow key={row.key} row={row} onToggleArea={onToggleArea} />
        ) : (
          <NodeRow key={row.key} row={row} onFocusNode={onFocusNode} />
        ),
      )}
    </>
  );
}

/** The rail: the connection diagram, global search, and ordinary incremental tree navigation. */
export function MemoryTreeRail({
  rows,
  loaded,
  focusedNodeId,
  query,
  onQuery,
  search,
  onToggleArea,
  onFocusNode,
  onFocusSearchResult,
  children,
}: MemoryTreeRailProps): React.JSX.Element {
  return (
    <aside className="mw-rail" aria-label="Knowledge tree">
      {children}
      <div className="mw-filter">
        <label>
          <Icon name="search" size={14} />
          <input
            type="text"
            value={query}
            maxLength={MAX_MEMORY_SEARCH_QUERY_CHARS}
            onChange={(event) => onQuery(event.currentTarget.value)}
            placeholder="Search memory"
            aria-label="Search memory titles and paths"
          />
        </label>
      </div>
      <nav className="mw-tree" aria-label="Knowledge tree" data-memory-tree>
        {search.active ? (
          <SearchRows search={search} focusedNodeId={focusedNodeId} onFocus={onFocusSearchResult} />
        ) : (
          <TreeRows
            rows={rows}
            loaded={loaded}
            onToggleArea={onToggleArea}
            onFocusNode={onFocusNode}
          />
        )}
      </nav>
    </aside>
  );
}
